import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { organizationClient } from '@/api'
import { UpdateOrganizationRequest } from '@/interfaces'

const BASE_QUERY_KEY = organizationClient.QUERY_KEY

export const useOrganizationInfo = (organizationId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, organizationId]
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const getOrganizationInfoQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => organizationClient.getOrganizationInfo(organizationId)
  })

  const removeOrganizationMutation = useMutation({
    mutationFn: () => organizationClient.removeOrganization(organizationId),
    onSuccess: () => {
      showSuccess('Organization successfully removed')
      navigate(`/administration`)
    }
  })

  const updateOrganizationMutation = useMutation({
    mutationFn: (request: UpdateOrganizationRequest) =>
      organizationClient.updateOrganization(organizationId, request),
    onSuccess: async () => {
      showSuccess('Organization successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    organizationInfo: getOrganizationInfoQuery.data,
    isFetchingOrganizationInfo: getOrganizationInfoQuery.isFetching,
    removeOrganization: removeOrganizationMutation.mutate,
    updateOrganization: updateOrganizationMutation.mutate,
    isUpdateInProgress: updateOrganizationMutation.isPending
  }
}
